import { DatePicker } from '@mui/lab';
import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { memo } from 'react';
import MunicipalityList from 'shell/components/shared/municipality-list';
import PmiTextField from 'shell/components/shared/text-field';
import { DocumentPaper } from 'shell/view-models/document-paper';

interface FormPaperProps {
  values?: DocumentPaper;
  onChange: (key: keyof DocumentPaper, value: DocumentPaper[keyof DocumentPaper]) => void;
  errors?: FormikErrors<DocumentPaper>;
  touched?: FormikTouched<DocumentPaper>;
  visibility?: { [K in keyof DocumentPaper]: boolean };
  disabled?: { [K in keyof DocumentPaper]: boolean };
  allDisabled?: boolean;
  isNotDisabledData?: boolean;
}

const FormPaperComponent = ({
  values,
  onChange,
  errors,
  touched,
  allDisabled,
  visibility = {
    transcribed: true,
    actDate: true,
  },
  disabled = {
    transcribed: false,
  },
  isNotDisabledData,
}: FormPaperProps): React.ReactElement => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={12} container spacing={1}>
        {visibility.actDate ? (
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data atto"
              aria-label="Data atto"
              value={values?.actDate || null}
              onChange={(date) => {
                onChange('actDate', date);
              }}
              disabled={isNotDisabledData ? false : allDisabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="actDate"
                  fullWidth
                  error={touched?.actDate && Boolean(errors?.actDate)}
                  helperText={errors?.actDate}
                />
              )}
            />
          </Grid>
        ) : undefined}
        <Grid item xs={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="transcript"
                disabled={disabled.transcribed || allDisabled}
                checked={values?.transcribed || false}
                onChange={() => onChange('transcribed', !values?.transcribed)}
                inputProps={{ 'aria-label': 'trascritto' }}
              />
            }
            label="Trascritto"
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={8}>
        <MunicipalityList
          aria-label="Comune"
          label="Comune"
          id="municipality"
          required
          disabled={allDisabled}
          fromDate={values?.actDate}
          value={values?.actMunicipality || null}
          onChange={(municipality) => onChange('actMunicipality', municipality)}
          error={touched?.actMunicipality && Boolean(errors?.actMunicipality)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Provincia"
          label="Provincia"
          id="province"
          name="province"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.actMunicipality?.province || ''}
          disabled
        />
      </Grid>
      <Grid item xs={12} container spacing={1}>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Anno"
            label="Anno *"
            id="year"
            name="year"
            fullWidth
            type="text"
            variant="outlined"
            inputProps={{
              maxLength: '4',
              pattern: '\\d{1,4}',
            }}
            value={values?.actYear || ''}
            onChange={(event) => onChange('actYear', event.target.value)}
            error={touched?.actYear && Boolean(errors?.actYear)}
            helperText={errors?.actYear}
            disabled={allDisabled}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Numero Atto"
            label="Nr. Atto *"
            id="documentNumber"
            name="documentNumber"
            fullWidth
            type="text"
            disabled={allDisabled}
            inputProps={{
              maxLength: '20',
            }}
            variant="outlined"
            value={values?.actNumber || ''}
            onChange={(event) => onChange('actNumber', event.target.value)}
            error={touched?.actNumber && Boolean(errors?.actNumber)}
            helperText={errors?.actNumber}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Parte"
            label="Parte *"
            id="portion"
            name="portion"
            fullWidth
            type="text"
            variant="outlined"
            disabled={allDisabled}
            inputProps={{
              maxLength: '1',
            }}
            value={values?.actPart || ''}
            onChange={(event) => onChange('actPart', event.target.value)}
            error={touched?.actPart && Boolean(errors?.actPart)}
            helperText={errors?.actPart}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Serie"
            label="Serie"
            id="serie"
            name="serie"
            fullWidth
            disabled={allDisabled}
            type="text"
            variant="outlined"
            value={values?.actSeries || ''}
            onChange={(event) => onChange('actSeries', event.target.value)}
            error={touched?.actSeries && Boolean(errors?.actSeries)}
            helperText={errors?.actSeries}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Volume"
            label="Volume"
            id="volume"
            name="volume"
            fullWidth
            disabled={allDisabled}
            type="text"
            inputProps={{
              maxLength: '3',
            }}
            variant="outlined"
            value={values?.actVolume || ''}
            onChange={(event) => onChange('actVolume', event.target.value)}
            error={touched?.actVolume && Boolean(errors?.actVolume)}
            helperText={errors?.actVolume}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

FormPaperComponent.displayName = 'Form Paper component';

const FormPaper = memo(FormPaperComponent);

export default FormPaper;
